/**
 * Assemble Financial Summary insights (Phase A rules + notices).
 */

import type {
  FinancialSummaryInsights,
  IncomeVsExpensesSection,
  PeriodComparisonSection,
  ReportDateRange,
} from '../../types/report.types';
import type { FinancialSummaryTransaction } from './financial-summary-dataset';
import {
  buildFinancialSummaryExpenseByCategory,
  buildBudgetPerformanceFromTransactions,
  buildIncomeVsExpensesSection,
  buildMonthlyTrendWithComparison,
  buildPeriodComparison,
} from './financial-summary-dataset';
import {
  analyseFinancialSummaryIncome,
  underlyingSavingsRate,
} from './financial-summary-income-analysis';
import {
  buildDataNoticesFromFindings,
  evaluateFinancialSummaryRules,
  isPriorPeriodComparisonReliable,
  nextStepsFromFindings,
  topKeyTakeaways,
} from './financial-summary-rules';
import type { ReportingCategoryRecord } from './reporting-transaction-flow';
import type { BudgetPeriodType } from '../../types/budget.types';

const BUDGET_CATEGORY_FOOTNOTE =
  'Category spending totals roll up to parent categories. Budget limits apply to the specific budget category (often a subcategory), so a parent total may differ from a single budget line.';

export function enrichIncomeVsExpensesWithOneOffSplit(
  base: IncomeVsExpensesSection,
  regularIncome: number,
  oneOffIncome: number
): IncomeVsExpensesSection {
  return {
    ...base,
    regularIncome,
    oneOffIncome,
    underlyingSavingsRate: underlyingSavingsRate(regularIncome, base.totalExpenses),
  };
}

export function buildFinancialSummaryInsights(input: {
  period: ReportDateRange;
  currentTransactions: FinancialSummaryTransaction[];
  priorTransactions: FinancialSummaryTransaction[];
  categoryMap: Map<string, { name: string }>;
  categories: ReportingCategoryRecord[];
  budgetLimits: Array<{ category_id: string; amount: number | string; period_type: BudgetPeriodType }>;
}): {
  incomeVsExpenses: IncomeVsExpensesSection;
  expenseByCategory: ReturnType<typeof buildFinancialSummaryExpenseByCategory>;
  budgetPerformance: ReturnType<typeof buildBudgetPerformanceFromTransactions>;
  monthlyTrend: ReturnType<typeof buildMonthlyTrendWithComparison>;
  periodComparison: PeriodComparisonSection;
  financialSummaryInsights: FinancialSummaryInsights;
} {
  const { period, currentTransactions, priorTransactions, categoryMap, categories, budgetLimits } =
    input;

  const incomeAnalysis = analyseFinancialSummaryIncome(currentTransactions, categories);
  const baseIncome = buildIncomeVsExpensesSection(currentTransactions, categories);
  const incomeVsExpenses = enrichIncomeVsExpensesWithOneOffSplit(
    baseIncome,
    incomeAnalysis.regularIncome,
    incomeAnalysis.oneOffIncome
  );

  const expenseByCategory = buildFinancialSummaryExpenseByCategory(
    currentTransactions,
    categoryMap,
    categories
  );
  const budgetPerformance = buildBudgetPerformanceFromTransactions(
    currentTransactions,
    budgetLimits,
    categoryMap,
    categories,
    period
  );
  const monthlyTrend = buildMonthlyTrendWithComparison(currentTransactions, categories);
  const periodComparison = buildPeriodComparison(
    period,
    currentTransactions,
    priorTransactions,
    categories
  );

  const comparisonReliable = isPriorPeriodComparisonReliable(
    currentTransactions,
    priorTransactions,
    periodComparison.priorTotalIncome,
    periodComparison.currentTotalIncome
  );
  periodComparison.comparisonReliable = comparisonReliable;

  const unknownRow = expenseByCategory.find((r) => r.categoryName === 'Unknown category');
  const uncategorisedRow = expenseByCategory.find((r) => r.categoryName === 'Uncategorised');

  const findings = evaluateFinancialSummaryRules({
    currentTransactions,
    priorTransactions,
    incomeAnalysis,
    totalExpenses: incomeVsExpenses.totalExpenses,
    expenseByCategory,
    budgetPerformance,
    monthlyTrend,
    periodComparison,
    unknownCategoryAmount: unknownRow?.amount ?? 0,
    unknownCategoryCount: unknownRow?.transactionCount ?? 0,
    uncategorisedAmount: uncategorisedRow?.amount ?? 0,
    uncategorisedCount: uncategorisedRow?.transactionCount ?? 0,
  });

  const footnotes: string[] = [];
  if (budgetPerformance.length > 0 && expenseByCategory.length > 0) {
    footnotes.push(BUDGET_CATEGORY_FOOTNOTE);
  }

  return {
    incomeVsExpenses,
    expenseByCategory,
    budgetPerformance,
    monthlyTrend,
    periodComparison,
    financialSummaryInsights: {
      dataNotices: buildDataNoticesFromFindings(findings, {
        currentTransactions,
        priorTransactions,
        incomeAnalysis,
        totalExpenses: incomeVsExpenses.totalExpenses,
        expenseByCategory,
        budgetPerformance,
        monthlyTrend,
        periodComparison,
        unknownCategoryAmount: unknownRow?.amount ?? 0,
        unknownCategoryCount: unknownRow?.transactionCount ?? 0,
        uncategorisedAmount: uncategorisedRow?.amount ?? 0,
        uncategorisedCount: uncategorisedRow?.transactionCount ?? 0,
      }),
      keyTakeaways: topKeyTakeaways(findings),
      nextSteps: nextStepsFromFindings(findings),
      footnotes,
    },
  };
}
